import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  FullScreenLoader,
  MainSectionLayout,
  MenuItem,
  ScrollToTop,
  Table,
  TableActionsMenu,
  TableHead,
  TabProps,
  UsersLayout,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import CallRecordingModal from '@Containers/CallRecording/CallRecordingModal';
import DialerSubAccountModal from '@Containers/DialerSubAccount/DialerSubAccountModal';
import { Dnc } from '@Containers/Dnc';
import { EmailsActive, EmailsArchived } from '@Containers/Emails';
import SMTPSettings from '@Containers/Emails/SMTPSettings';
import { ScriptsActive, ScriptsArchived } from '@Containers/Scripts';
import TelnyxRotationNumbersContainer from '@Containers/UserCallerIds/TelnyxRotationCallerIds/TelnyxRotationNumbersMain';
import TwilioRotationNumbersContainer from '@Containers/UserCallerIds/TwilioRotationCallerIds/TwilioRotationNumbersMain';
import VonageCallerIds from '@Containers/UserCallerIds/VonageCallerIds';
import VonageRotationCallerIdsContainer from '@Containers/UserCallerIds/VonageRotationCallerIds/VonageRotationCallerIdsContainer';
import VoiceProviderModal from '@Containers/VoiceProvider/VoiceProviderModal';
import { VoiceProvider } from '@Models/callerIds.models';
import { UsersListRequestAndResponse } from '@Models/users.models';
import { useGetUsersListQuery } from '@Services/users.service';
import {
  getRowsPerPageOptions,
  getSortingIcon,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';
import useVoiceProvider from '@Utils/hooks/useVoiceProvider.hook';

import { ModuleName } from './Users.types';
import { getRows } from './Users.utils';

const Users: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  /** STATES */
  const [searchValue, setSearchValue] = useState<string>('');
  const [showFilterBtn, setShowFilterBtn] = useState(false);
  const [userId, setUserId] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [isOpenCallRecordingModal, setIsOpenCallRecordingModal] =
    useState<boolean>(false);
  const [isOpenVoiceProviderModal, setIsOpenVoiceProviderModal] =
    useState<boolean>(false);
  const [isOpenDialerSubAccountModal, setIsOpenDialerSubAccountModal] =
    useState<boolean>(false);
  const [moduleName, setModuleName] = useState<ModuleName | null>(
    query.get(CommonConstants.QUERY_PARAMS.USERS.MODULE) as ModuleName
  );

  const { userVoiceProvider } = useVoiceProvider(userId);

  /** Syncs the state according to URL Query Params */
  const syncState = useCallback(() => {
    /** URL PARAMS */
    // Supports search with field "searchTerm" in fields "id", "firstName", "lastName", "email".
    const searchParam =
      query.get(CommonConstants.QUERY_PARAMS.COMMON.SEARCH) || '';
    // User Id
    const userIdParam =
      query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    // User Name
    const userNameParam =
      query.get(CommonConstants.QUERY_PARAMS.USERS.USER_NAME) || '';
    // Module Param (Scrips, Emails, DNC, TPR, CallRecording)
    const moduleParam =
      (query.get(CommonConstants.QUERY_PARAMS.USERS.MODULE) as ModuleName) ||
      '';

    /** SET STATES */
    setSearchValue(`${searchParam}`);
    setShowFilterBtn(!!searchParam);
    setUserId(`${userIdParam}`);
    setUserName(`${userNameParam}`);
    setModuleName(moduleParam);
  }, []);

  useEffect(() => {
    syncState();
  }, [syncState, location.search]);

  /** EDGE CASE
   * Adds default query params to the url if not added (pageNo, pageSize, order (sort-order), sortDir (sort-direction)).
   * */
  useEffect(() => {
    if (!moduleName) {
      const routeParams = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) ||
          CommonConstants.INITIAL_PAGE_NO,
        [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) ||
          CommonConstants.INITIAL_ROWS_PER_PAGE,
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
          query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) ||
          CommonConstants.INITIAL_USERS_SORT_ORDER,
        [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
          query.get(CommonConstants.QUERY_PARAMS.COMMON.DIRECTION) ||
          CommonConstants.INITIAL_USERS_SORT_DIRECTION,
      });

      changeRoute(routeParams);
    }
  }, [changeRoute, moduleName]);

  /** QUERIES (Changes when URL Query Params changes) */
  const usersQueries: UsersListRequestAndResponse = useMemo(() => {
    const queries = query.parse(location.search);

    return {
      searchTerm:
        (queries[CommonConstants.QUERY_PARAMS.COMMON.SEARCH] as string) || '',
      pagination: {
        pageNumber:
          (queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO] as number) ||
          CommonConstants.INITIAL_PAGE_NO,
        objPerPage:
          (queries[CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE] as number) ||
          CommonConstants.INITIAL_ROWS_PER_PAGE,
      },
      sort: {
        field:
          (queries[CommonConstants.QUERY_PARAMS.COMMON.ORDER] as string) || '',
        direction:
          (queries[CommonConstants.QUERY_PARAMS.COMMON.DIRECTION] as string) ||
          CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC,
      },
    };
  }, [location.search]);

  /** RTK QUERY HOOK */
  const { data, isLoading, isFetching } = useGetUsersListQuery(usersQueries, {
    skip: !!userId,
  });

  /** Table Loading State */
  const tableLoading = isLoading || isFetching;

  const modalWindows = [
    ModuleName.CallRecording,
    ModuleName.VoiceProvider,
    ModuleName.DialerSubAccount,
  ];

  /** Handle Actions Row */
  const handleActionsForUser = (
    accountId: string,
    accountName: string,
    moduleType: ModuleName,
    hasTpr?: boolean,
    hasUserLiveConnect?: boolean
  ) => {
    setUserId(accountId);
    setUserName(accountName);
    setModuleName(moduleType);

    const params = query.stringify({
      userId: accountId,
      userName: accountName,
      module: moduleType,
      pageSize: CommonConstants.INITIAL_ROWS_PER_PAGE,
      pageNo: CommonConstants.INITIAL_PAGE_NO,
      hasUserTpr: hasTpr?.toString() || '',
      hasUserLiveConnect: hasUserLiveConnect?.toString() || '',
    });

    changeRoute(params);
  };

  /** Handle Modal Actions Row */
  const handleModalActionsForUser = (
    accountId: string,
    accountName: string,
    moduleType: ModuleName
  ) => {
    setUserId(accountId);
    setUserName(accountName);
    setModuleName(moduleType);

    switch (moduleType) {
      case ModuleName.CallRecording:
        setIsOpenCallRecordingModal(true);
        break;
      case ModuleName.VoiceProvider:
        setIsOpenVoiceProviderModal(true);
        break;
      case ModuleName.DialerSubAccount:
        setIsOpenDialerSubAccountModal(true);
        break;
      default:
    }
  };

  /** Table Actions Component */
  const actionsMenuComponent = (
    accountId: string,
    accountName: string,
    hasTpr: boolean,
    hasLiveConnectSuite: boolean,
    admin: boolean
  ): JSX.Element => {
    /** Actions Menu Data */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.actionsMenu.Manage Scripts'),
        icon: 'speakerNotes',
        value: accountId,
        onClick: () =>
          handleActionsForUser(accountId, accountName, ModuleName.Scripts),
      },
      {
        label: t('common.actionsMenu.Manage DNC'),
        icon: 'block',
        value: accountId,
        onClick: () =>
          handleActionsForUser(accountId, accountName, ModuleName.DNC),
      },
      {
        label: t('common.actionsMenu.Manage Email Templates'),
        icon: 'mail',
        value: accountId,
        onClick: () =>
          handleActionsForUser(accountId, accountName, ModuleName.Emails),
      },
      {
        label: t('common.actionsMenu.Manage Voice Provider'),
        icon: 'voiceProvider',
        value: accountId,
        onClick: () =>
          handleModalActionsForUser(
            accountId,
            accountName,
            ModuleName.VoiceProvider
          ),
      },
      {
        label: t('common.actionsMenu.Manage Call Recording'),
        icon: 'callRecording',
        value: accountId,
        onClick: () =>
          handleModalActionsForUser(
            accountId,
            accountName,
            ModuleName.CallRecording
          ),
      },
    ];

    if (hasLiveConnectSuite) {
      actionsMenu.push({
        label: t('common.actionsMenu.Manage Dialer SubAccount'),
        icon: 'dialerSubAccount',
        value: accountId,
        onClick: () =>
          handleModalActionsForUser(
            accountId,
            accountName,
            ModuleName.DialerSubAccount
          ),
      });
    }

    if (!admin) {
      actionsMenu.push({
        label: t('common.actionsMenu.Manage Caller Ids'),
        icon: 'phone',
        value: accountId,
        onClick: () =>
          handleActionsForUser(
            accountId,
            accountName,
            ModuleName.TPR,
            hasTpr,
            hasLiveConnectSuite
          ),
      });
    }

    return <TableActionsMenu menuItems={actionsMenu} />;
  };

  /** Rows, Total Rows Count */
  const rows = getRows(data?.data, actionsMenuComponent);
  const totalRowsCount = data?.total || 0;

  /** Pagination Select Rows Options */
  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    totalRowsCount
  );

  /** Filter Handler
   * Handles Search Button based Search.
   */
  const onSearch = (value: string) => {
    const searchParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.SEARCH]: value,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });
    changeRoute(searchParam);
  };

  /** Clear Filters Handler */
  const handleClearFilterBtnClick = () => {
    const params = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.SEARCH]: '',
    });

    changeRoute(params);
  };

  /** Pagination Handler
   * Handles Page Change.
   */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const pageParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );
    changeRoute(pageParam);
  };

  /** Pagination Handler
   * Handles RowsPerChange Change.
   */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const pageParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });
    changeRoute(pageParams);
  };

  /* * Table Head for List of Users Table */
  const getTableHead = (): TableHead[] => {
    const orderParam =
      query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) || '';
    const dirParam =
      query.get(CommonConstants.QUERY_PARAMS.COMMON.DIRECTION) || '';

    const tableHeadOnClick = (order: string) => {
      const params = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]: order,
        [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
          dirParam === CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
            ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.DESC
            : CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC,
      });
      changeRoute(params);
    };

    const getIcon = (order: string) =>
      getSortingIcon(order === orderParam ? `${dirParam}` : '');

    return [
      {
        label: t('Users.Account ID'),
        icon: getIcon(CommonConstants.QUERY_PARAMS.USERS.SORT_BY.ID),
        align: 'left',
        width: 13,
        onClick: () =>
          tableHeadOnClick(CommonConstants.QUERY_PARAMS.USERS.SORT_BY.ID),
      },
      {
        label: t('Users.User Name'),
        icon: getIcon(CommonConstants.QUERY_PARAMS.USERS.SORT_BY.FULL_NAME),
        align: 'left',
        width: 38.8,
        onClick: () =>
          tableHeadOnClick(
            CommonConstants.QUERY_PARAMS.USERS.SORT_BY.FULL_NAME
          ),
      },
      {
        label: t('Users.User Type'),
        icon: getIcon(CommonConstants.QUERY_PARAMS.USERS.SORT_BY.ADMIN),
        align: 'left',
        width: 12.4,
        onClick: () =>
          tableHeadOnClick(CommonConstants.QUERY_PARAMS.USERS.SORT_BY.ADMIN),
      },
      {
        label: t('Users.Email Address'),
        icon: getIcon(CommonConstants.QUERY_PARAMS.USERS.SORT_BY.EMAIL),
        width: 41.2,
        align: 'left',
        onClick: () =>
          tableHeadOnClick(CommonConstants.QUERY_PARAMS.USERS.SORT_BY.EMAIL),
      },
      {
        label: t('common.table.Actions'),
        width: 8.5,
        align: 'center',
      },
    ];
  };

  /** DNC Table Component */
  const TableComponent = (
    <Table
      rows={rows}
      tableHead={getTableHead()}
      paginationProps={{
        page:
          ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
            CommonConstants.INITIAL_PAGE_NO) - 1,
        rowsPerPage:
          (query.get(
            CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE
          ) as number) || CommonConstants.INITIAL_ROWS_PER_PAGE,
        count: totalRowsCount,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
      }}
      loading={tableLoading}
    />
  );
  /** Table */

  /** IMPERSONATE --> */
  const backBtnOnClick = (): void => {
    const search = query.setAll({
      [CommonConstants.QUERY_PARAMS.USERS.USER_ID]: '',
      [CommonConstants.QUERY_PARAMS.USERS.USER_NAME]: '',
      [CommonConstants.QUERY_PARAMS.USERS.MODULE]: '',
      [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
        CommonConstants.INITIAL_USERS_SORT_ORDER,
      [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
        CommonConstants.INITIAL_USERS_SORT_DIRECTION,
    });
    setModuleName(null);
    changeRoute(search);
  };

  const getTabProps = (module?: ModuleName): TabProps | undefined => {
    switch (module) {
      case ModuleName.Scripts:
        return {
          labels: [t('common.messages.Active'), t('common.messages.Archived')],
          values: [<ScriptsActive />, <ScriptsArchived />],
        };
      case ModuleName.Emails:
        return {
          labels: [
            t('common.messages.Active'),
            t('common.messages.Archived'),
            t('Email.SMTP Settings'),
          ],
          values: [<EmailsActive />, <EmailsArchived />, <SMTPSettings />],
        };
      default:
        return undefined;
    }
  };

  const getFilterProps = (module?: ModuleName): ReactNode | undefined => {
    switch (module) {
      case ModuleName.DNC:
        return <Dnc />;
      case ModuleName.TPR:
        if (userVoiceProvider) {
          if (userVoiceProvider === VoiceProvider.TWILIO) {
            return <TwilioRotationNumbersContainer isAdmin />;
          }
          if (userVoiceProvider === VoiceProvider.TELNYX) {
            return <TelnyxRotationNumbersContainer isAdmin />;
          }
          return (
            <>
              <VonageCallerIds isAdmin />
              <VonageRotationCallerIdsContainer isAdmin />
            </>
          );
        }
        break;
      default:
        return undefined;
    }
  };
  /** <-- IMPERSONATE */

  return !isLoading ? (
    <>
      {moduleName && !modalWindows.includes(moduleName) ? (
        <>
          <MainSectionLayout
            heading={t(`Users.manage_${moduleName || ''}`, {
              username: userName,
            })}
            backBtnProps={
              userId
                ? {
                    text: t('common.messages.Back'),
                    onClick: backBtnOnClick,
                  }
                : undefined
            }
            tabProps={getTabProps(moduleName)}
            filters={getFilterProps(moduleName)}
          />
        </>
      ) : (
        <>
          <ScrollToTop
            dependencies={[
              query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
              query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
            ]}
          />
          <UsersLayout
            searchProps={{
              value: searchValue,
              setValue: setSearchValue,
              placeholder: t('common.tableFilters.Search by name'),
              onSearch,
            }}
            table={TableComponent}
            showFilterControls={showFilterBtn}
            clearFilterBtnOnClick={handleClearFilterBtnClick}
          />
          {isOpenCallRecordingModal && (
            <CallRecordingModal
              isOpen={isOpenCallRecordingModal}
              setIsOpen={setIsOpenCallRecordingModal}
              userId={userId}
              userName={userName}
            />
          )}
          {isOpenVoiceProviderModal && (
            <VoiceProviderModal
              isOpen={isOpenVoiceProviderModal}
              setIsOpen={setIsOpenVoiceProviderModal}
              userId={userId}
              userName={userName}
            />
          )}
          {isOpenDialerSubAccountModal && (
            <DialerSubAccountModal
              isOpen={isOpenDialerSubAccountModal}
              setIsOpen={setIsOpenDialerSubAccountModal}
              userId={userId}
              userName={userName}
            />
          )}
        </>
      )}
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default Users;
